import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2, ChevronRight, ChevronDown, ExternalLink, MessageSquare, Send, Plus, Search, X, TrendingUp, TrendingDown, LayoutList, LayoutGrid, Check, Flame, AlertTriangle as AlertTriangleIcon, Minus, ArrowUp, ArrowDown, User, Filter, Hash } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import type { Id } from '../../../../convex/_generated/dataModel'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

type Severity = 'P0' | 'P1' | 'P2'
type IssueStatus = 'open' | 'investigating' | 'in_progress' | 'resolved' | 'wontfix' | 'regressed'
type SortKey = 'recent' | 'count' | 'lastSeen' | 'affectedUsers' | 'severity' | 'assignee' | 'created' | 'issueNumber'
type SortDir = 'asc' | 'desc'
type GroupBy = 'none' | 'severity' | 'status' | 'category' | 'assignee' | 'creator' | 'source'
type IssueSource = 'auto' | 'manual' | 'snapshot' | 'user_report' | 'content_feedback'
type SourceFilter = 'ALL' | IssueSource

type Occurrence = {
  workflowId: Id<'workflows'>
  shortcode?: string
  projectTitle: string
  userEmail: string
  sessionScore: number
  findingSeverity: 'minor' | 'moderate' | 'severe'
  description: string
  evidence: string
  timestampMs?: number
  occurredAt?: number
  userGoal?: string
}

type Issue = {
  id: string
  issueNumber?: number
  title: string
  category: string
  severity: Severity
  rootCauseHypothesis: string
  userImpact?: string
  reproSteps?: string
  suggestedInvestigation?: string
  userReactionPattern?: string
  firstSeen?: number
  lastSeen?: number
  affectedUsers?: number
  avgSessionScore?: number
  skills?: { name: string; count: number }[]
  tags?: string[]
  occurrenceCount: number
  recentOccurrenceCount?: number
  signalScore?: number
  source?: IssueSource
  description?: string
  createdBy?: string
}

type TrackingEntry = {
  status: string
  assignee?: string
  // Lite: count only. Full comments + status history are fetched per-issue
  // (getIssueTrackingDetail) when an issue is opened — keeps the list query small.
  commentCount: number
  updatedAt: number
}

type AdminUser = {
  userId: string
  name?: string
  email?: string
  image?: string
}

const ISSUE_STATUSES: IssueStatus[] = ['open', 'investigating', 'in_progress', 'resolved', 'regressed', 'wontfix']

const SEV_STYLE: Record<Severity, string> = {
  P0: 'bg-red-50 text-red-700 border-red-200',
  P1: 'bg-amber-50 text-amber-700 border-amber-200',
  P2: 'bg-neutral-100 text-neutral-600 border-neutral-200',
}

const SEV_BORDER: Record<Severity, string> = {
  P0: 'border-l-red-500',
  P1: 'border-l-amber-400',
  P2: 'border-l-neutral-300',
}

const STATUS_STYLE: Record<IssueStatus, string> = {
  open: 'bg-white text-neutral-600 border-neutral-200',
  investigating: 'bg-cyan-50/80 text-cyan-700 border-cyan-200/60',
  in_progress: 'bg-amber-50/80 text-amber-700 border-amber-200/60',
  resolved: 'bg-neutral-100/80 text-neutral-400 border-neutral-200/60',
  wontfix: 'bg-neutral-50 text-neutral-400 border-neutral-200/40',
  regressed: 'bg-red-50/80 text-red-600 border-red-200/60',
}

const STATUS_DOT: Record<IssueStatus, string> = {
  open: 'bg-neutral-400',
  investigating: 'bg-cyan-500',
  in_progress: 'bg-amber-500',
  resolved: 'bg-neutral-500',
  wontfix: 'bg-neutral-300',
  regressed: 'bg-red-500',
}

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: 'Open',
  investigating: 'Investigating',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  wontfix: "Won't Fix",
  regressed: 'Regressed',
}

function StatusGlyph({ status, className = '' }: { status: IssueStatus; className?: string }) {
  if (status === 'resolved') {
    return <Check className={`h-2.5 w-2.5 shrink-0 text-neutral-500 ${className}`} strokeWidth={3} />
  }
  return <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[status]} ${className}`} />
}

const FINDING_SEV_STYLE: Record<string, string> = {
  severe: 'bg-red-50 text-red-700',
  moderate: 'bg-amber-50 text-amber-700',
  minor: 'bg-neutral-100 text-neutral-600',
}

function formatTime(ts: number) {
  const diffMs = Date.now() - ts
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatCommentTime(ts: number) {
  const d = new Date(ts)
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const relative = formatTime(ts)
  return `${date} at ${time} (${relative})`
}

function formatShortDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Compact span like "3h", "5d", "2wk", "1mo", "1.2y" — for Age and Last-seen columns.
function formatDuration(ms: number) {
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return mins < 1 ? 'now' : `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 14) return `${days}d`
  if (days < 60) return `${Math.round(days / 7)}wk`
  if (days < 365) return `${Math.round(days / 30)}mo`
  return `${(days / 365).toFixed(1)}y`
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.split(' ')
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase()
  }
  return (email || '?').slice(0, 2).toUpperCase()
}

const AVATAR_COLORS = ['bg-blue-600', 'bg-violet-600', 'bg-emerald-600', 'bg-amber-600', 'bg-rose-600', 'bg-cyan-600']
function avatarColor(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function Avatar({ user, size = 22, className = '' }: { user?: AdminUser | { name?: string; email?: string; image?: string; userId?: string }; size?: number; className?: string }) {
  if (!user) {
    return <span className={`rounded-full border border-dashed border-neutral-200/60 bg-neutral-50/50 shrink-0 ${className}`} style={{ width: size, height: size }} />
  }
  const id = (user as AdminUser).userId || user.email || user.name || '?'
  if (user.image) {
    return <img src={user.image} alt="" className={`rounded-full object-cover shrink-0 ring-1 ring-neutral-200/40 ${className}`} style={{ width: size, height: size }} title={user.name || user.email} />
  }
  return (
    <span className={`rounded-full flex items-center justify-center font-bold text-white shrink-0 ring-1 ring-black/5 ${avatarColor(id)} ${className}`} style={{ width: size, height: size, fontSize: size * 0.38 }} title={user.name || user.email}>
      {getInitials(user.name, user.email)}
    </span>
  )
}

function SeverityIcon({ severity, className = '' }: { severity: Severity; className?: string }) {
  if (severity === 'P0') return <Flame className={`h-3 w-3 text-red-500 ${className}`} />
  if (severity === 'P1') return <AlertTriangleIcon className={`h-3 w-3 text-amber-500 ${className}`} />
  return <Minus className={`h-3 w-3 text-neutral-400 ${className}`} />
}

const SOURCE_LABEL: Record<string, string> = { auto: 'Auto', manual: 'Manual', snapshot: 'Snapshot', user_report: 'User report', content_feedback: 'Content' }
const SOURCE_STYLE: Record<string, string> = {
  auto: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  manual: 'bg-indigo-50 text-indigo-600 border-indigo-100',
  snapshot: 'bg-blue-50 text-blue-600 border-blue-100',
  // Human-reported signals get a stronger, warmer treatment so they read as
  // higher-priority than auto-mined findings (weight 10 / 5 respectively).
  user_report: 'bg-rose-100 text-rose-700 border-rose-200 font-bold',
  content_feedback: 'bg-amber-50 text-amber-700 border-amber-200',
}
// Source-ordering used by filter dropdown + group order. Human signals first.
const SOURCE_ORDER: IssueSource[] = ['user_report', 'content_feedback', 'auto', 'manual', 'snapshot']

// Compact origin badge. Auto is the implicit default, so we hide it to keep rows
// quiet — only non-auto origins (human reports, manual, snapshots) get a chip.
function SourceBadge({ source }: { source?: IssueSource }) {
  if (!source || source === 'auto') return null
  return (
    <span className={cn('text-[8.5px] font-semibold uppercase tracking-wider px-1.5 py-[1px] rounded-md border shrink-0', SOURCE_STYLE[source])}>
      {SOURCE_LABEL[source] ?? source}
    </span>
  )
}

// Weighted-priority chip shown next to the raw occurrence count. Only renders
// when the weighted score diverges from the count (i.e. a heavier-than-auto
// signal contributed), so plain auto issues stay uncluttered.
function SignalChip({ signalScore, occurrenceCount }: { signalScore?: number; occurrenceCount: number }) {
  if (signalScore == null || signalScore <= occurrenceCount) return null
  return (
    <span
      className="text-[9px] font-bold text-rose-600 bg-rose-50 border border-rose-200/70 px-1.5 py-[1px] rounded-md tabular-nums font-mono shrink-0"
      title={`Signal score ${signalScore} (weighted by source) vs ${occurrenceCount} raw occurrences`}
    >
      signal {signalScore}
    </span>
  )
}

const GROUP_LABELS: Record<GroupBy, string> = {
  none: 'No grouping',
  severity: 'Severity',
  status: 'Status',
  category: 'Category',
  assignee: 'Assignee',
  creator: 'Creator',
  source: 'Source',
}

export default function AdminIssuesPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const [searchParams, setSearchParams] = useSearchParams()

  const severityFilter = (searchParams.get('sev') || 'ALL') as Severity | 'ALL'
  const categoryFilter = searchParams.get('cat') || 'ALL'
  const statusFilter = (searchParams.get('status') || 'ACTIVE') as IssueStatus | 'ALL' | 'ACTIVE'
  const skillFilter = searchParams.get('skill') || 'ALL'
  const tagFilter = searchParams.get('tag') || 'ALL'
  const assigneeFilter = searchParams.get('assignee') || 'ALL'
  const searchQuery = searchParams.get('q') || ''
  const sortKey = (searchParams.get('sort') || 'recent') as SortKey
  const sortDir = (searchParams.get('dir') || 'desc') as SortDir
  const sourceFilter = (searchParams.get('source') || 'ALL') as SourceFilter
  const creatorFilter = searchParams.get('creator') || 'ALL'
  const groupBy = (searchParams.get('group') || 'none') as GroupBy

  const setFilter = useCallback((key: string, val: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (val === 'ALL' || val === '' || val === 'recent' || val === 'none' || val === 'desc') next.delete(key)
      else next.set(key, val)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setSeverityFilter = (v: Severity | 'ALL') => setFilter('sev', v)
  const setCategoryFilter = (v: string) => setFilter('cat', v)
  const setStatusFilter = (v: IssueStatus | 'ALL' | 'ACTIVE') => setSearchParams((prev) => {
    const next = new URLSearchParams(prev)
    if (v === 'ACTIVE') next.delete('status')
    else next.set('status', v)
    return next
  }, { replace: true })
  const setSkillFilter = (v: string) => setFilter('skill', v)
  const setTagFilter = (v: string) => setFilter('tag', v)
  const setAssigneeFilter = (v: string) => setFilter('assignee', v)
  const setSearchQuery = (v: string) => setFilter('q', v)
  const setSortKey = (v: SortKey) => setFilter('sort', v)
  const setSortDir = (v: SortDir) => setFilter('dir', v)
  const setSourceFilter = (v: SourceFilter) => setFilter('source', v)
  const setCreatorFilter = (v: string) => setFilter('creator', v)
  const setGroupBy = (v: GroupBy) => setFilter('group', v)

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [denseMode, setDenseMode] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const admins = useQuery(
    api.admin.listAdmins,
    isAdmin ? {} : 'skip',
  ) as AdminUser[] | undefined

  const queryArgs = useMemo(() => (isAdmin ? {} : 'skip'), [isAdmin])
  const { results: issueRows, status: paginationStatus, loadMore } = usePaginatedQuery(
    api.admin.listGlobalIssuesSummary,
    queryArgs as any,
    { initialNumItems: 200 },
  )
  const isLoadingFirst = paginationStatus === 'LoadingFirstPage'
  const canLoadMore = paginationStatus === 'CanLoadMore'

  useEffect(() => {
    if (paginationStatus === "CanLoadMore") loadMore(200)
  }, [paginationStatus, loadMore])

  const issues = useMemo<Issue[]>(() => {
    if (!issueRows) return []
    return issueRows.map((r: any) => ({
      id: r.issueId,
      issueNumber: r.issueNumber,
      title: r.title,
      category: r.category,
      severity: r.severity,
      rootCauseHypothesis: r.rootCauseHypothesis,
      userImpact: r.userImpact,
      reproSteps: r.reproSteps,
      suggestedInvestigation: r.suggestedInvestigation,
      userReactionPattern: r.userReactionPattern,
      firstSeen: r.firstSeen,
      lastSeen: r.lastSeen,
      affectedUsers: r.affectedUsers,
      avgSessionScore: r.avgSessionScore,
      skills: r.skills,
      tags: r.tags,
      occurrenceCount: r.occurrenceCount ?? 0,
      recentOccurrenceCount: r.recentOccurrenceCount,
      signalScore: r.signalScore,
      source: r.source,
      description: r.description,
      createdBy: r.createdBy,
    }))
  }, [issueRows])

  // Lite status/comment-count map, built from the paginated summary rows (which
  // now carry per-issue tracking) — avoids a global issueTracking collect.
  const tracking = useMemo<Record<number, TrackingEntry>>(() => {
    const m: Record<number, TrackingEntry> = {}
    for (const r of (issueRows ?? []) as any[]) {
      if (r.issueNumber != null && r.tracking) m[r.issueNumber] = r.tracking
    }
    return m
  }, [issueRows])

  const categories = useMemo(() => {
    const set = new Set<string>()
    issues.forEach((b) => set.add(b.category))
    return Array.from(set).sort()
  }, [issues])

  const allSkills = useMemo(() => {
    const counts = new Map<string, number>()
    issues.forEach((b) => b.skills?.forEach((s) => counts.set(s.name, (counts.get(s.name) ?? 0) + 1)))
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }))
  }, [issues])

  const allTags = useMemo(() => {
    const counts = new Map<string, number>()
    issues.forEach((b) => b.tags?.forEach((t) => counts.set(t, (counts.get(t) ?? 0) + 1)))
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }))
  }, [issues])

  const allCreators = useMemo(() => {
    const set = new Set<string>()
    issues.forEach((b) => { if (b.createdBy) set.add(b.createdBy) })
    return Array.from(set).sort()
  }, [issues])

  const allSources = useMemo(() => {
    const counts: Record<string, number> = {}
    issues.forEach((b) => { const s = b.source || 'auto'; counts[s] = (counts[s] ?? 0) + 1 })
    return counts
  }, [issues])

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    const f = issues.filter((b) => {
      if (q && !b.title.toLowerCase().includes(q) && !b.rootCauseHypothesis.toLowerCase().includes(q)
        && !(b.issueNumber != null && `iss-${b.issueNumber}`.includes(q))
        && !(b.description?.toLowerCase().includes(q))
        && !(b.tags?.some(t => t.toLowerCase().includes(q)))) return false
      if (severityFilter !== 'ALL' && b.severity !== severityFilter) return false
      if (categoryFilter !== 'ALL' && b.category !== categoryFilter) return false
      if (sourceFilter !== 'ALL' && (b.source || 'auto') !== sourceFilter) return false
      if (creatorFilter !== 'ALL' && b.createdBy !== creatorFilter) return false
      {
        const s = (tracking?.[b.issueNumber ?? 0]?.status ?? 'open') as IssueStatus
        if (statusFilter === 'ACTIVE') {
          if (s === 'resolved' || s === 'wontfix') return false
        } else if (statusFilter !== 'ALL' && s !== statusFilter) return false
      }
      if (skillFilter !== 'ALL' && !b.skills?.some((s) => s.name === skillFilter)) return false
      if (tagFilter !== 'ALL' && !b.tags?.includes(tagFilter)) return false
      if (assigneeFilter !== 'ALL') {
        const a = tracking?.[b.issueNumber ?? 0]?.assignee
        if (assigneeFilter === 'UNASSIGNED') { if (a) return false }
        else { if (a !== assigneeFilter) return false }
      }
      return true
    })
    const sevRank: Record<Severity, number> = { P0: 0, P1: 1, P2: 2 }
    const dir = sortDir === 'asc' ? 1 : -1
    const sorted = [...f]
    // dir already encodes direction (desc = -1); each comparator expresses its
    // metric so that desc lands the "most important" row first.
    // "count" ranks by effective signal (signalScore ?? occurrenceCount) so a
    // weighted user report outranks a pile of auto findings; ties fall back to raw count.
    // "recent" (default) ranks by 7-day activity so long-quiet issues sink —
    // all-time counts pinned fixed issues to the top of the list forever.
    if (sortKey === 'recent') sorted.sort((a, b) => dir * (((a.recentOccurrenceCount ?? 0) - (b.recentOccurrenceCount ?? 0)) || ((a.signalScore ?? a.occurrenceCount) - (b.signalScore ?? b.occurrenceCount))))
    else if (sortKey === 'count') sorted.sort((a, b) => dir * (((a.signalScore ?? a.occurrenceCount) - (b.signalScore ?? b.occurrenceCount)) || (a.occurrenceCount - b.occurrenceCount)))
    else if (sortKey === 'lastSeen') sorted.sort((a, b) => dir * ((a.lastSeen ?? 0) - (b.lastSeen ?? 0)))
    else if (sortKey === 'affectedUsers') sorted.sort((a, b) => dir * ((a.affectedUsers ?? 0) - (b.affectedUsers ?? 0)))
    // severity rank is inverted (P0 = 0 = most severe), so flip the diff to keep desc = P0 first.
    else if (sortKey === 'severity') sorted.sort((a, b) => dir * (sevRank[b.severity] - sevRank[a.severity]) || b.occurrenceCount - a.occurrenceCount)
    else if (sortKey === 'assignee') sorted.sort((a, b) => {
      const aa = tracking?.[a.issueNumber ?? 0]?.assignee ?? ''
      const bb = tracking?.[b.issueNumber ?? 0]?.assignee ?? ''
      return dir * ((bb ? 1 : 0) - (aa ? 1 : 0) || aa.localeCompare(bb))
    })
    else if (sortKey === 'created') sorted.sort((a, b) => dir * ((a.firstSeen ?? 0) - (b.firstSeen ?? 0)))
    else if (sortKey === 'issueNumber') sorted.sort((a, b) => dir * ((a.issueNumber ?? 0) - (b.issueNumber ?? 0)))
    return sorted
  }, [issues, severityFilter, categoryFilter, statusFilter, skillFilter, tagFilter, assigneeFilter, searchQuery, sortKey, sortDir, sourceFilter, creatorFilter, tracking])

  const grouped = useMemo<{ key: string; label: string; items: Issue[] }[]>(() => {
    if (groupBy === 'none') return [{ key: '__all', label: '', items: filtered }]
    const groups = new Map<string, Issue[]>()
    const getKey = (b: Issue): string => {
      if (groupBy === 'severity') return b.severity
      if (groupBy === 'status') return (tracking?.[b.issueNumber ?? 0]?.status ?? 'open')
      if (groupBy === 'category') return b.category
      if (groupBy === 'assignee') return tracking?.[b.issueNumber ?? 0]?.assignee || 'Unassigned'
      if (groupBy === 'creator') return b.createdBy || 'Unknown'
      if (groupBy === 'source') return b.source || 'auto'
      return '__all'
    }
    for (const b of filtered) {
      const k = getKey(b)
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k)!.push(b)
    }
    const order = groupBy === 'severity' ? ['P0', 'P1', 'P2'] :
      groupBy === 'status' ? ISSUE_STATUSES as string[] :
      groupBy === 'source' ? (SOURCE_ORDER as string[]) : undefined
    const entries = Array.from(groups.entries())
    if (order) entries.sort((a, b) => (order.indexOf(a[0]) - order.indexOf(b[0])))
    else entries.sort((a, b) => b[1].length - a[1].length)
    return entries.map(([k, items]) => ({
      key: k,
      label: groupBy === 'status' ? (STATUS_LABEL[k as IssueStatus] || k) :
        groupBy === 'source' ? (SOURCE_LABEL[k] || k) :
        groupBy === 'assignee' && k !== 'Unassigned' ? (admins?.find(a => a.userId === k)?.name || admins?.find(a => a.userId === k)?.email || k) :
        k,
      items,
    }))
  }, [filtered, groupBy, tracking, admins])

  const totals = useMemo(() => {
    const sev: Record<Severity, number> = { P0: 0, P1: 0, P2: 0 }
    const status: Record<IssueStatus, number> = { open: 0, investigating: 0, in_progress: 0, resolved: 0, wontfix: 0, regressed: 0 }
    let openP0 = 0
    let active7d = 0
    let unassigned = 0
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
    issues.forEach((b) => {
      sev[b.severity] += 1
      const s = (tracking?.[b.issueNumber ?? 0]?.status ?? 'open') as IssueStatus
      status[s] += 1
      if (b.severity === 'P0' && (s === 'open' || s === 'regressed')) openP0++
      if ((b.lastSeen ?? 0) > sevenDaysAgo && s !== 'resolved' && s !== 'wontfix') active7d++
      if (!tracking?.[b.issueNumber ?? 0]?.assignee && s !== 'resolved' && s !== 'wontfix') unassigned++
    })
    const totalOccurrences = issues.reduce((sum, b) => sum + b.occurrenceCount, 0)
    return { sev, status, openP0, active7d, unassigned, regressed: status.regressed, active: issues.length - status.resolved - status.wontfix, totalOccurrences }
  }, [issues, tracking])

  // Shared time axis for the per-row lifespan sparks, so rows are comparable.
  const timeWindow = useMemo(() => {
    let min = Infinity
    for (const b of issues) {
      if (typeof b.firstSeen === 'number') min = Math.min(min, b.firstSeen)
    }
    const end = Date.now()
    const start = Number.isFinite(min) ? Math.min(min, end - 24 * 60 * 60 * 1000) : end - 14 * 24 * 60 * 60 * 1000
    return { start, end }
  }, [issues])

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(b => b.issueNumber != null ? `iss-${b.issueNumber}` : b.id)))
  }, [selected.size, filtered])

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const copyIssueId = useCallback((num: number) => {
    navigator.clipboard.writeText(`ISS-${num}`)
    setCopiedId(`iss-${num}`)
    setTimeout(() => setCopiedId(null), 1500)
  }, [])

  const setStatusMutation = useMutation(api.admin.setIssueStatus)
  const assignMutation = useMutation(api.admin.assignIssue)

  const bulkSetStatus = useCallback(async (status: IssueStatus) => {
    const nums = Array.from(selected).map(id => {
      const m = id.match(/^iss-(\d+)$/)
      return m ? Number(m[1]) : null
    }).filter((n): n is number => n != null)
    await Promise.all(nums.map(n => setStatusMutation({ issueNumber: n, status })))
    setSelected(new Set())
  }, [selected, setStatusMutation])

  const bulkAssign = useCallback(async (userId: string) => {
    const nums = Array.from(selected).map(id => {
      const m = id.match(/^iss-(\d+)$/)
      return m ? Number(m[1]) : null
    }).filter((n): n is number => n != null)
    await Promise.all(nums.map(n => assignMutation({ issueNumber: n, assignee: userId || undefined })))
    setSelected(new Set())
  }, [selected, assignMutation])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); return }
      if (e.key === 'j') { setFocusIndex((i) => Math.min(i + 1, filtered.length - 1)); return }
      if (e.key === 'k') { setFocusIndex((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'c') { setShowCreateModal(true); return }
      if (e.key === '?') { setShowHelpModal(h => !h); return }
      if (e.key === 'Escape') { setExpanded(new Set()); setSelected(new Set()); setFocusIndex(-1); setShowHelpModal(false); return }
      if (e.key === 'x' && focusIndex >= 0 && focusIndex < filtered.length) {
        const b = filtered[focusIndex]
        const rowKey = b.issueNumber != null ? `iss-${b.issueNumber}` : b.id
        toggleSelect(rowKey)
        return
      }
      if (e.key === 'Enter' && focusIndex >= 0 && focusIndex < filtered.length) {
        const b = filtered[focusIndex]
        const rowKey = b.issueNumber != null ? `iss-${b.issueNumber}` : b.id
        toggle(rowKey)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [focusIndex, filtered, toggleSelect])

  useEffect(() => {
    if (focusIndex >= 0 && listRef.current) {
      const el = listRef.current.children[focusIndex] as HTMLElement | undefined
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [focusIndex])

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  if (isLoadingFirst) {
    return (
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-5 w-20 bg-neutral-200/60 rounded-md animate-pulse" />
            <div className="h-3 w-48 bg-neutral-100/60 rounded-md animate-pulse mt-2" />
          </div>
          <div className="h-8 w-24 bg-neutral-200/40 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-5 gap-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-neutral-200/40 bg-white px-3.5 py-2.5 animate-pulse">
              <div className="h-6 w-10 bg-neutral-100/80 rounded-md" />
              <div className="h-2.5 w-16 bg-neutral-100/60 rounded mt-2" />
            </div>
          ))}
        </div>
        <div className="h-9 bg-neutral-100/40 rounded-lg animate-pulse" />
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-neutral-200/40 bg-white px-3.5 py-3 flex items-center gap-3" style={{ opacity: 1 - i * 0.1 }}>
              <div className="h-3.5 w-3.5 bg-neutral-100/60 rounded animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-neutral-200/50 rounded-md animate-pulse" style={{ width: `${70 - i * 5}%` }} />
                <div className="h-2.5 bg-neutral-100/40 rounded-md animate-pulse w-40" />
              </div>
              <div className="h-5 w-14 bg-neutral-100/40 rounded-md animate-pulse" />
              <div className="h-5 w-8 bg-neutral-100/40 rounded-md animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const isStillLoading = paginationStatus === 'LoadingMore'

  if (issues.length === 0) {
    return (
      <div className="p-6 space-y-5">
        <div>
          <h1 className="text-[17px] font-bold text-neutral-900 tracking-tight">Issues</h1>
          <p className="text-[12px] text-neutral-400 mt-1">No issues found.</p>
        </div>
        <div className="rounded-lg border border-neutral-200/60 bg-white p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
            <Search className="h-5 w-5 text-neutral-300" />
          </div>
          <p className="text-[13px] text-neutral-500">Trigger a meta analysis from <Link to="/admin/meta-analysis-runs" className="text-blue-500 hover:text-blue-600 font-medium">Meta-Analysis Runs</Link>.</p>
        </div>
      </div>
    )
  }

  const activeFilterCount = [severityFilter !== 'ALL', statusFilter !== 'ACTIVE', categoryFilter !== 'ALL', skillFilter !== 'ALL', tagFilter !== 'ALL', assigneeFilter !== 'ALL', sourceFilter !== 'ALL', creatorFilter !== 'ALL'].filter(Boolean).length

  return (
    <div className="p-5 space-y-3.5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[17px] font-bold text-neutral-900 tracking-tight leading-none">Issues</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelpModal(true)}
            className="text-neutral-300 hover:text-neutral-500 p-1.5 rounded-md hover:bg-neutral-100 transition-colors"
            title="Keyboard shortcuts (?)"
          >
            <kbd className="font-mono text-[10px] font-bold">?</kbd>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 text-[11px] font-semibold px-3.5 py-[7px] rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 shadow-sm hover:shadow transition-all"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            New Issue
          </button>
        </div>
      </div>

      {/* Search + filter bar (sticky) */}
      <div className="sticky top-0 z-10 bg-neutral-50/80 backdrop-blur-md -mx-5 px-5 py-2.5 space-y-2 border-b border-neutral-200/50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-300" />
        <input
          ref={searchRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search issues, tags, descriptions...  /"
          className="w-full text-[12px] border border-neutral-200/80 rounded-lg pl-9 pr-8 py-2 bg-white/80 placeholder:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-200 transition-shadow"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-300 hover:text-neutral-500 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Status tabs — primary navigation */}
      <div className="flex items-center gap-0 border-b border-neutral-200/60 -mx-1">
        <StatusTab active={statusFilter === 'ACTIVE'} onClick={() => setStatusFilter('ACTIVE')} count={totals.active}>Active</StatusTab>
        <StatusTab active={statusFilter === 'ALL'} onClick={() => setStatusFilter('ALL')}>All</StatusTab>
        {ISSUE_STATUSES.map((s) => (
          <StatusTab key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} count={totals.status[s]} dim={totals.status[s] === 0}>
            <StatusGlyph status={s} className="mr-1" />
            {STATUS_LABEL[s]}
          </StatusTab>
        ))}
      </div>

      {/* Filter row — secondary controls */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <FilterSelect value={severityFilter} onChange={(v) => setSeverityFilter(v as Severity | 'ALL')} placeholder="Severity" options={[
            { value: 'P0', label: `P0 Critical (${totals.sev.P0})` },
            { value: 'P1', label: `P1 Warning (${totals.sev.P1})` },
            { value: 'P2', label: `P2 Low (${totals.sev.P2})` },
          ]} />
          <FilterSelect value={categoryFilter} onChange={setCategoryFilter} placeholder="Category" options={categories.map((c) => ({ value: c, label: c }))} />
          <FilterSelect value={sourceFilter} onChange={(v) => setSourceFilter(v as SourceFilter)} placeholder="Source" options={SOURCE_ORDER.filter(s => allSources[s]).map(s => ({ value: s, label: `${SOURCE_LABEL[s]} (${allSources[s]})` }))} />
          {admins && admins.length > 0 && (
            <FilterSelect
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              placeholder="Assignee"
              options={[
                { value: 'UNASSIGNED', label: 'Unassigned' },
                ...admins.map((a) => ({ value: a.userId, label: a.name || a.email || a.userId })),
              ]}
            />
          )}
          {allSkills.length > 0 && (
            <FilterSelect value={skillFilter} onChange={setSkillFilter} placeholder="Skill" options={allSkills.map((s) => ({ value: s.name, label: `${s.name} (${s.count})` }))} />
          )}
          {allTags.length > 0 && (
            <FilterSelect value={tagFilter} onChange={setTagFilter} placeholder="Tag" options={allTags.map((t) => ({ value: t.name, label: `${t.name} (${t.count})` }))} />
          )}
          {allCreators.length > 0 && (
            <FilterSelect value={creatorFilter} onChange={setCreatorFilter} placeholder="Creator" options={allCreators.map(c => ({ value: c, label: c.replace(/@.*/, '') }))} />
          )}
        </div>

        <div className="ml-auto flex items-center gap-1.5 border-l border-neutral-200/40 pl-3">
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            className={`text-[10px] border rounded-md px-2 py-[5px] cursor-pointer transition-colors ${
              groupBy !== 'none' ? 'border-violet-200 bg-violet-50/50 text-violet-700 font-medium' : 'border-neutral-200/80 bg-white text-neutral-500 hover:border-neutral-300'
            }`}
          >
            {(Object.keys(GROUP_LABELS) as GroupBy[]).map(g => (
              <option key={g} value={g}>{g === 'none' ? 'Group' : `Group: ${GROUP_LABELS[g]}`}</option>
            ))}
          </select>

          <button
            onClick={() => setDenseMode(!denseMode)}
            className={`p-1 rounded-md border transition-all duration-150 ${denseMode ? 'bg-neutral-800 text-white border-neutral-800 shadow-sm' : 'bg-white text-neutral-400 border-neutral-200/80 hover:bg-neutral-50 hover:text-neutral-600'}`}
            title={denseMode ? 'Card view' : 'Dense view'}
          >
            {denseMode ? <LayoutList className="h-3.5 w-3.5" /> : <LayoutGrid className="h-3.5 w-3.5" />}
          </button>

          <div className="flex items-center">
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="text-[10px] border border-neutral-200/80 rounded-l-md px-2 py-[5px] bg-white text-neutral-500 border-r-0 cursor-pointer hover:border-neutral-300"
            >
              <option value="recent">Active (7d)</option>
              <option value="count">Occurrences</option>
              <option value="lastSeen">Last seen</option>
              <option value="created">Created</option>
              <option value="affectedUsers">Users</option>
              <option value="severity">Severity</option>
              <option value="assignee">Assignee</option>
              <option value="issueNumber">Issue #</option>
            </select>
            <button
              onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}
              className="border border-neutral-200/80 rounded-r-md px-1.5 py-[5px] bg-white text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50 transition-colors"
              title={sortDir === 'desc' ? 'Descending' : 'Ascending'}
            >
              {sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Active filter chips */}
      <ActiveFilters
        filters={[
          severityFilter !== 'ALL' ? { label: `Severity: ${severityFilter}`, clear: () => setSeverityFilter('ALL') } : null,
          statusFilter !== 'ACTIVE' ? { label: `Status: ${statusFilter === 'ALL' ? 'All' : STATUS_LABEL[statusFilter as IssueStatus]}`, clear: () => setStatusFilter('ACTIVE') } : null,
          categoryFilter !== 'ALL' ? { label: `Category: ${categoryFilter}`, clear: () => setCategoryFilter('ALL') } : null,
          sourceFilter !== 'ALL' ? { label: `Source: ${SOURCE_LABEL[sourceFilter]}`, clear: () => setSourceFilter('ALL') } : null,
          creatorFilter !== 'ALL' ? { label: `Creator: ${creatorFilter.replace(/@.*/, '')}`, clear: () => setCreatorFilter('ALL') } : null,
          skillFilter !== 'ALL' ? { label: `Skill: ${skillFilter}`, clear: () => setSkillFilter('ALL') } : null,
          tagFilter !== 'ALL' ? { label: `Tag: ${tagFilter}`, clear: () => setTagFilter('ALL') } : null,
          assigneeFilter !== 'ALL' ? { label: `Assignee: ${assigneeFilter === 'UNASSIGNED' ? 'Unassigned' : admins?.find((a) => a.userId === assigneeFilter)?.name || assigneeFilter}`, clear: () => setAssigneeFilter('ALL') } : null,
        ].filter(Boolean) as { label: string; clear: () => void }[]}
        clearAll={() => {
          setSeverityFilter('ALL'); setStatusFilter('ACTIVE'); setCategoryFilter('ALL')
          setSkillFilter('ALL'); setTagFilter('ALL'); setAssigneeFilter('ALL'); setSearchQuery('')
          setSourceFilter('ALL'); setCreatorFilter('ALL')
        }}
        count={activeFilterCount}
      />

      {/* Dense mode table header */}
      {denseMode && filtered.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 text-[8.5px] uppercase tracking-[0.08em] text-neutral-400 border-b border-neutral-200/60 bg-neutral-50/80 rounded-t-lg font-semibold select-none">
          <span className="w-5 shrink-0">
            <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} className="h-3 w-3 rounded border-neutral-300 text-neutral-800 cursor-pointer" />
          </span>
          <span className="w-12 shrink-0">ID</span>
          <span className="w-2 shrink-0" />
          <span className="flex-1">Title</span>
          <span className="w-20 shrink-0">Creator</span>
          <span className="w-24 shrink-0 text-right">Category</span>
          <span className="w-12 shrink-0 text-center">Trend</span>
          <span className="w-5 shrink-0" />
          <span className="w-10 shrink-0 text-right">#</span>
          <span className="w-4 shrink-0" />
        </div>
      )}

      {/* Issue list */}
      <div ref={listRef} className={denseMode ? 'space-y-px' : 'space-y-2'}>
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
              <Search className="h-5 w-5 text-neutral-300" />
            </div>
            <div className="text-[13px] font-semibold text-neutral-600">No matching issues</div>
            <div className="text-[11.5px] text-neutral-400 mt-1.5 max-w-[280px] mx-auto leading-relaxed">Try adjusting your filters or search query to find what you're looking for</div>
            <button
              onClick={() => {
                setSeverityFilter('ALL'); setStatusFilter('ACTIVE'); setCategoryFilter('ALL')
                setSkillFilter('ALL'); setTagFilter('ALL'); setAssigneeFilter('ALL'); setSearchQuery('')
                setSourceFilter('ALL'); setCreatorFilter('ALL')
              }}
              className="mt-4 text-[11px] text-blue-600 hover:text-blue-700 font-semibold px-4 py-2 rounded-lg border border-blue-200/60 hover:bg-blue-50/80 transition-all hover:shadow-sm"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          grouped.map((group) => {
            const isGroupCollapsed = collapsedGroups.has(group.key)
            return (
              <div key={group.key}>
                {/* Group header */}
                {groupBy !== 'none' && (
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center gap-2 py-2.5 px-1.5 text-left group/gh hover:bg-neutral-50/50 rounded-md transition-colors"
                  >
                    {isGroupCollapsed ? <ChevronRight className="h-3 w-3 text-neutral-300 group-hover/gh:text-neutral-500 transition-colors" /> : <ChevronDown className="h-3 w-3 text-neutral-400" />}
                    <span className="text-[11px] font-bold text-neutral-600 tracking-tight">{group.label}</span>
                    <span className="text-[9.5px] font-mono tabular-nums text-neutral-400 bg-neutral-100/80 px-1.5 py-[1px] rounded-md font-semibold">{group.items.length}</span>
                    <span className="flex-1 border-t border-neutral-100/60" />
                  </button>
                )}

                {!isGroupCollapsed && (
                  <div className={denseMode ? 'space-y-px' : 'space-y-2'}>
                    {group.items.map((b) => {
                      const globalIdx = filtered.indexOf(b)
                      const rowKey = b.issueNumber != null ? `iss-${b.issueNumber}` : b.id
                      const isOpen = expanded.has(rowKey)
                      const isFocused = globalIdx === focusIndex
                      const isSelected = selected.has(rowKey)
                      const tr = tracking?.[b.issueNumber ?? 0]
                      const issueStatus = (tr?.status ?? 'open') as IssueStatus
                      const commentCount = tr?.commentCount ?? 0
                      const assignee = tr?.assignee
                      const assigneeAdmin = assignee ? admins?.find((a) => a.userId === assignee) : undefined

                      if (denseMode) {
                        return (
                          <div key={rowKey} className={`border-l-[3px] ${SEV_BORDER[b.severity]} ${isFocused ? 'ring-2 ring-blue-200/60' : ''} ${isSelected ? 'bg-blue-50/30' : ''} ${issueStatus === 'resolved' ? 'opacity-45' : ''}`}>
                            <div className={`w-full flex items-center gap-2 px-3 py-[7px] text-left text-[12px] group transition-colors ${isOpen ? 'bg-neutral-100/80' : 'bg-white hover:bg-neutral-50/60'}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(rowKey)}
                                className="h-3 w-3 rounded border-neutral-300 text-neutral-800 cursor-pointer shrink-0"
                              />
                              <button onClick={() => toggle(rowKey)} className="flex items-center gap-2 flex-1 min-w-0">
                                <SeverityIcon severity={b.severity} />
                                <span className="text-[10px] font-mono text-neutral-400 w-12 shrink-0 tabular-nums">{b.issueNumber != null ? `ISS-${b.issueNumber}` : ''}</span>
                                <StatusGlyph status={issueStatus} />
                                <span className="truncate font-medium text-neutral-800">{b.title}</span>
                                <SourceBadge source={b.source} />
                                <span className="flex-1" />
                                {b.createdBy && (
                                  <span className="text-[10px] text-neutral-400 shrink-0 w-20 truncate">{b.createdBy.replace(/@.*/, '')}</span>
                                )}
                                <span className="text-[10px] text-neutral-400 shrink-0 w-24 text-right truncate">{b.category}</span>
                                <TimeSpark issue={b} start={timeWindow.start} end={timeWindow.end} />
                                <Avatar user={assigneeAdmin} size={20} />
                                <SignalChip signalScore={b.signalScore} occurrenceCount={b.occurrenceCount} />
                                <span className="text-[10px] font-bold text-neutral-500 bg-neutral-100/60 px-1.5 py-[2px] rounded-md min-w-[28px] text-center tabular-nums font-mono">
                                  {b.occurrenceCount}
                                </span>
                                <TrendIndicator firstSeen={b.firstSeen} lastSeen={b.lastSeen} />
                              </button>
                            </div>
                            {isOpen && (
                              <IssueDetail
                                issue={b}
                                trackingEntry={tr}
                                userName={user?.name || user?.email || 'admin'}
                                admins={admins}
                              />
                            )}
                          </div>
                        )
                      }

                      return (
                        <div key={rowKey} className={`rounded-lg border border-neutral-200/60 bg-white overflow-hidden border-l-[3px] ${SEV_BORDER[b.severity]} transition-all duration-200 hover:border-neutral-300/80 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)] ${isFocused ? 'ring-2 ring-blue-200/60 shadow-sm border-blue-200' : ''} ${isSelected ? 'ring-2 ring-blue-300/50 bg-blue-50/20 border-blue-200' : ''} ${issueStatus === 'resolved' ? 'opacity-50 hover:opacity-100' : ''}`}>
                          <div className="w-full flex items-start gap-2.5 px-3.5 py-3 hover:bg-neutral-50/30 text-left group">
                            {/* Checkbox */}
                            <div className="mt-1 shrink-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(rowKey)}
                                className="h-3.5 w-3.5 rounded border-neutral-300 text-neutral-800 cursor-pointer"
                              />
                            </div>

                            {/* Severity icon */}
                            <div className="mt-0.5 shrink-0">
                              <SeverityIcon severity={b.severity} />
                            </div>

                            {/* Expand toggle */}
                            <button onClick={() => toggle(rowKey)} className="mt-0.5 shrink-0 transition-colors">
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-400 transition-colors" />}
                            </button>

                            {/* Main content */}
                            <button onClick={() => toggle(rowKey)} className="flex-1 min-w-0 space-y-1 text-left">
                              {/* Line 1: ISS-N + Title */}
                              <div className="flex items-center gap-2">
                                {b.issueNumber != null && (
                                  <span
                                    onClick={(e) => { e.stopPropagation(); copyIssueId(b.issueNumber!) }}
                                    className="text-[9.5px] font-mono text-neutral-300 shrink-0 tabular-nums font-semibold hover:text-blue-500 transition-colors flex items-center gap-0.5 cursor-pointer"
                                    title="Copy issue ID"
                                    role="button"
                                  >
                                    ISS-{b.issueNumber}
                                    {copiedId === `iss-${b.issueNumber}` && <Check className="h-2.5 w-2.5 text-emerald-500" />}
                                  </span>
                                )}
                                <span className="text-[12.5px] font-semibold text-neutral-800 truncate leading-snug tracking-tight">{b.title}</span>
                                <SourceBadge source={b.source} />
                                <TrendIndicator firstSeen={b.firstSeen} lastSeen={b.lastSeen} />
                              </div>

                              {/* Line 2: Description preview */}
                              {b.description && (
                                <div className="text-[11px] text-neutral-400 truncate leading-snug">{b.description.slice(0, 120)}</div>
                              )}

                              {/* Line 3: Metadata */}
                              <div className="flex items-center gap-1.5 text-[10px] text-neutral-400 flex-wrap">
                                <span className="font-mono text-neutral-500/80 font-medium">{b.category}</span>
                                {b.createdBy && (
                                  <>
                                    <span className="w-px h-2.5 bg-neutral-200/60" />
                                    <span className="text-neutral-400 inline-flex items-center gap-0.5">
                                      <User className="h-2.5 w-2.5 opacity-50" />
                                      {b.createdBy.replace(/@.*/, '')}
                                    </span>
                                  </>
                                )}
                                {typeof b.affectedUsers === 'number' && (
                                  <>
                                    <span className="w-px h-2.5 bg-neutral-200/60" />
                                    <span>{b.affectedUsers} {b.affectedUsers === 1 ? 'user' : 'users'}</span>
                                  </>
                                )}
                                {typeof b.avgSessionScore === 'number' && (
                                  <>
                                    <span className="w-px h-2.5 bg-neutral-200/60" />
                                    <span className="tabular-nums">{b.avgSessionScore}/10</span>
                                  </>
                                )}
                              </div>

                              {/* Tags row */}
                              {((b.tags && b.tags.length > 0) || (b.skills && b.skills.length > 0)) && (
                                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                  {b.tags?.slice(0, 4).map(t => (
                                    <span key={t} className="text-[9px] px-1.5 py-[1px] rounded-md bg-sky-50/80 text-sky-600 border border-sky-100/60 inline-flex items-center gap-0.5 font-medium">
                                      <Hash className="h-2 w-2 opacity-50" />
                                      {t}
                                    </span>
                                  ))}
                                  {b.skills?.slice(0, 3).map(s => (
                                    <span key={s.name} className="text-[9px] px-1.5 py-[1px] rounded-md bg-violet-50/80 text-violet-600/80 border border-violet-100/60 font-medium">
                                      {s.name} <span className="text-violet-400/70 ml-0.5">{s.count}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </button>

                            {/* Right side: temporal columns + status + assignee + count */}
                            <div className="flex items-center gap-3.5 shrink-0">
                              {/* Temporal: last seen / age / lifespan */}
                              <div className="hidden md:flex flex-col gap-1 shrink-0 w-[152px]">
                                <div className="flex items-start justify-between gap-3 leading-none">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-[8px] uppercase tracking-wider text-neutral-300 font-semibold">Last seen</span>
                                    <span className="text-[11px] text-neutral-600 font-mono tabular-nums">{typeof b.lastSeen === 'number' ? `${formatDuration(Date.now() - b.lastSeen)} ago` : '—'}</span>
                                  </div>
                                  <div className="flex flex-col items-end gap-0.5">
                                    <span className="text-[8px] uppercase tracking-wider text-neutral-300 font-semibold">Age</span>
                                    <span className="text-[11px] text-neutral-600 font-mono tabular-nums">{typeof b.firstSeen === 'number' ? formatDuration(Date.now() - b.firstSeen) : '—'}</span>
                                  </div>
                                </div>
                                <TimeSpark issue={b} start={timeWindow.start} end={timeWindow.end} width={152} />
                              </div>

                              <span className={`inline-flex items-center gap-1 text-[9.5px] font-semibold px-2 py-[3px] rounded-md border ${STATUS_STYLE[issueStatus]}`}>
                                <StatusGlyph status={issueStatus} />
                                {STATUS_LABEL[issueStatus]}
                              </span>

                              <Avatar user={assigneeAdmin} size={22} />

                              {commentCount > 0 && (
                                <span className="flex items-center gap-0.5 text-[10px] text-neutral-300 font-mono tabular-nums">
                                  <MessageSquare className="h-3 w-3" />{commentCount}
                                </span>
                              )}

                              <SignalChip signalScore={b.signalScore} occurrenceCount={b.occurrenceCount} />

                              <span className={`text-[10.5px] font-bold px-2 py-[3px] rounded-md min-w-[32px] text-center font-mono tabular-nums leading-none ${
                                b.occurrenceCount >= 100 ? 'bg-neutral-800 text-white shadow-sm' :
                                b.occurrenceCount >= 30 ? 'bg-neutral-200/80 text-neutral-700' :
                                'bg-neutral-100/80 text-neutral-500'
                              }`}>
                                {b.occurrenceCount}
                              </span>

                              {b.issueNumber != null && (
                                <Link
                                  to={`/admin/issues/${b.issueNumber}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-neutral-300 hover:text-blue-500 shrink-0 opacity-0 group-hover:opacity-100 transition-all p-0.5 rounded hover:bg-blue-50/50"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Link>
                              )}
                            </div>
                          </div>

                          {isOpen && (
                            <IssueDetail
                              issue={b}
                              trackingEntry={tr}
                              userName={user?.name || user?.email || 'admin'}
                              admins={admins}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {(isStillLoading || canLoadMore) && (
        <div className="flex justify-center items-center gap-2 text-[10px] text-neutral-300 py-3">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span className="font-mono tabular-nums">{issues.length} loaded</span>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-neutral-900 text-white rounded-xl shadow-2xl shadow-neutral-900/20 px-5 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-200 border border-neutral-800">
          <span className="text-[12px] font-bold tabular-nums">{selected.size} <span className="text-neutral-400 font-normal">selected</span></span>
          <span className="h-5 w-px bg-neutral-700" />
          <div className="flex items-center gap-0.5">
            <span className="text-[9px] text-neutral-500 mr-1.5 uppercase tracking-wider font-semibold">Status</span>
            {ISSUE_STATUSES.map(s => (
              <button key={s} onClick={() => bulkSetStatus(s)} className="text-[10px] px-2 py-1 rounded-md hover:bg-neutral-700/80 transition-colors font-medium">
                <StatusGlyph status={s} className="mr-1" />
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
          {admins && admins.length > 0 && (
            <>
              <span className="h-5 w-px bg-neutral-700" />
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] text-neutral-500 uppercase tracking-wider font-semibold">Assign</span>
                <select
                  onChange={(e) => { if (e.target.value) bulkAssign(e.target.value) }}
                  className="text-[10px] bg-neutral-800 text-white border border-neutral-700 rounded-md px-2 py-1 cursor-pointer hover:border-neutral-600 transition-colors"
                  defaultValue=""
                >
                  <option value="" disabled>Select...</option>
                  <option value="">Unassign</option>
                  {admins.map(a => <option key={a.userId} value={a.userId}>{a.name || a.email}</option>)}
                </select>
              </div>
            </>
          )}
          <span className="h-5 w-px bg-neutral-700" />
          <button onClick={() => setSelected(new Set())} className="text-neutral-500 hover:text-white transition-colors p-1 rounded-md hover:bg-neutral-700/80">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Keyboard hints */}
      <div className="flex items-center justify-center gap-3 text-[9px] text-neutral-300 py-3 select-none opacity-0 hover:opacity-60 transition-opacity duration-300">
        <Kbd k="j" label="/" k2="k" label2="navigate" />
        <Kbd k="↵" label2="expand" />
        <Kbd k="x" label2="select" />
        <Kbd k="c" label2="create" />
        <Kbd k="/" label2="search" />
        <Kbd k="?" label2="help" />
        <Kbd k="esc" label2="clear" />
      </div>

      {showCreateModal && (
        <CreateIssueModal
          admins={admins}
          categories={categories}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showHelpModal && <KeyboardShortcutsModal onClose={() => setShowHelpModal(false)} />}
    </div>
  )
}

// ─── Time spark (lifespan on a shared axis: found → last occurred) ───────────

function TimeSpark({ issue, start, end, width = 64 }: { issue: Issue; start: number; end: number; width?: number }) {
  const { firstSeen, lastSeen } = issue
  if (typeof firstSeen !== 'number' || typeof lastSeen !== 'number' || end <= start) {
    return <span style={{ width }} className="shrink-0 inline-block" />
  }
  const span = end - start
  const w = width
  const h = 16
  const cy = h / 2
  const clamp = (t: number) => Math.max(0, Math.min(w, ((t - start) / span) * w))
  const fx = clamp(firstSeen)
  const lx = Math.max(clamp(lastSeen), fx) // last is never before first

  const dayMs = 24 * 60 * 60 * 1000
  const age = end - lastSeen
  // Color by recency of the last occurrence.
  const color = age < 3 * dayMs ? '#ef4444' : age < 7 * dayMs ? '#f59e0b' : age < 14 * dayMs ? '#737373' : '#d4d4d4'
  const spanDays = Math.max(0, Math.round((lastSeen - firstSeen) / dayMs))

  const tip = `Found ${formatShortDate(firstSeen)} · last seen ${formatTime(lastSeen)}`
    + (spanDays > 0 ? ` · active ${spanDays}d` : ' · single day')
    + ` · ${issue.occurrenceCount}×`

  return (
    <span title={tip} className="shrink-0 inline-flex">
      <svg width={w} height={h} className="overflow-visible">
        {/* full window track */}
        <line x1={0} y1={cy} x2={w} y2={cy} stroke="#ececec" strokeWidth={1} strokeLinecap="round" />
        {/* active lifespan */}
        <line x1={fx} y1={cy} x2={lx} y2={cy} stroke={color} strokeWidth={2.5} strokeLinecap="round" opacity={0.55} />
        {/* found marker (hollow) */}
        <circle cx={fx} cy={cy} r={2.4} fill="white" stroke={color} strokeWidth={1.3} />
        {/* last-seen marker (filled) */}
        <circle cx={lx} cy={cy} r={2.6} fill={color} />
      </svg>
    </span>
  )
}

// ─── Trend indicator (recent activity direction) ─────────────────────────────

function TrendIndicator({ lastSeen }: { firstSeen?: number; lastSeen?: number }) {
  if (!lastSeen) return null
  const now = Date.now()
  const ageMs = now - lastSeen
  const threeDays = 3 * 24 * 60 * 60 * 1000
  const sevenDays = 7 * 24 * 60 * 60 * 1000
  const fourteenDays = 14 * 24 * 60 * 60 * 1000

  if (ageMs < threeDays) {
    return <span className="shrink-0" aria-label="Active in last 3 days"><TrendingUp className="h-3 w-3 text-red-400" /></span>
  }
  if (ageMs < sevenDays) {
    return <span className="shrink-0" aria-label="Active in last 7 days"><TrendingUp className="h-3 w-3 text-amber-400" /></span>
  }
  if (ageMs > fourteenDays) {
    return <span className="shrink-0" aria-label="Stale (>14 days)"><TrendingDown className="h-3 w-3 text-neutral-300" /></span>
  }
  return null
}

// ─── Occurrence timeline (dot-stack histogram + event markers) ────────────────
// Each occurrence is one dot. Dots stack vertically within fine time bins, so
// the height of a column equals the true count in that bin — accurate volume
// over time, with point-level fidelity (no aggregation into fudged bars).

type EventMarker = { ts: number; type: 'status' | 'comment'; label: string; detail: string; color: string }
type StackedDot = { ts: number; sev: string; email: string; desc: string; cx: number; stackIdx: number }

const SEV_COLOR: Record<string, string> = { severe: '#dc2626', moderate: '#d97706', minor: '#a3a3a3' }
const SEV_ORDER: Record<string, number> = { severe: 0, moderate: 1, minor: 2 }

function volumeTicks(max: number): number[] {
  if (max <= 1) return [0, 1]
  const step = max <= 4 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : max <= 60 ? 10 : max <= 150 ? 25 : Math.ceil(max / 4 / 50) * 50
  const out: number[] = []
  for (let v = 0; v <= max; v += step) out.push(v)
  return out
}

function OccurrenceTimeline({
  occurrences,
  statusHistory,
  comments,
  totalCount,
}: {
  occurrences: Array<{ timestampMs?: number; occurredAt?: number; findingSeverity?: string; userEmail?: string; description?: string }>
  statusHistory?: { status: string; timestamp: number; author?: string }[]
  comments?: { text: string; author: string; createdAt: number }[]
  totalCount?: number
}) {
  const [hoverX, setHoverX] = useState<number | null>(null)
  const [hoverEvent, setHoverEvent] = useState<number | null>(null)
  const [mouseX, setMouseX] = useState(0)
  const [containerW, setContainerW] = useState(800)

  const w = 1200
  const h = 132
  const axisY = h - 16
  const plotTop = 8
  const plotH = axisY - plotTop
  const colPitch = 6
  const numCols = Math.max(1, Math.floor(w / colPitch))

  const data = useMemo(() => {
    const dayMs = 86400000

    const raw = occurrences
      .map(o => ({ ts: o.occurredAt ?? o.timestampMs ?? 0, sev: o.findingSeverity || 'minor', email: o.userEmail || '', desc: o.description || '' }))
      .filter(e => e.ts > 0)
    const sts = (statusHistory || []).filter(s => s.timestamp > 0)
    const cmts = (comments || []).filter(c => c.createdAt > 0)

    const allTs = [...raw.map(e => e.ts), ...sts.map(e => e.timestamp), ...cmts.map(e => e.createdAt)]
    if (allTs.length === 0) return null

    const minTs = Math.min(...allTs)
    const maxTs = Math.max(...allTs)
    const span = Math.max(maxTs - minTs, dayMs)
    const pad = span * 0.02
    const startTs = minTs - pad
    const range = span + pad * 2

    // Severity order so severe stacks at the bottom of each column
    const sorted = [...raw].sort((a, b) => (SEV_ORDER[a.sev] ?? 2) - (SEV_ORDER[b.sev] ?? 2))

    // Bin into pixel columns; stack within each column
    const colItems: typeof sorted[] = Array.from({ length: numCols }, () => [])
    for (const o of sorted) {
      const col = Math.max(0, Math.min(numCols - 1, Math.floor(((o.ts - startTs) / range) * numCols)))
      colItems[col].push(o)
    }
    let maxStack = 1
    const dots: StackedDot[] = []
    colItems.forEach((items, col) => {
      if (items.length > maxStack) maxStack = items.length
      const cx = (col + 0.5) * (w / numCols)
      items.forEach((o, j) => dots.push({ ...o, cx, stackIdx: j }))
    })

    const rangeDays = range / dayMs
    const tickCount = Math.min(8, Math.max(2, Math.ceil(rangeDays > 14 ? rangeDays / 7 : rangeDays)))
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
      const ts = startTs + (i / tickCount) * range
      return { ts, label: new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
    })

    const events: EventMarker[] = []
    for (const s of sts) {
      const color = s.status === 'resolved' ? '#059669' : s.status === 'regressed' ? '#dc2626' : s.status === 'investigating' || s.status === 'in_progress' ? '#d97706' : '#737373'
      events.push({ ts: s.timestamp, type: 'status', label: STATUS_LABEL[s.status as IssueStatus] || s.status, detail: s.author ? `by ${s.author}` : '', color })
    }
    for (const c of cmts) {
      events.push({ ts: c.createdAt, type: 'comment', label: c.author, detail: c.text.slice(0, 60), color: '#2563eb' })
    }
    events.sort((a, b) => a.ts - b.ts)

    const sc = { severe: 0, moderate: 0, minor: 0 }
    raw.forEach(e => { if (e.sev in sc) sc[e.sev as keyof typeof sc]++ })

    return { dots, events, startTs, range, ticks, sc, plotted: raw.length, maxStack }
  }, [occurrences, statusHistory, comments, numCols])

  if (!data) return null
  const { dots, events, startTs, range, ticks, sc, plotted, maxStack } = data
  const displayTotal = totalCount ?? plotted

  // Global dot pitch so the tallest stack fits exactly: height = count × pitch
  const dotPitch = Math.min(5, plotH / maxStack)
  const dotR = Math.max(0.9, Math.min(2.2, dotPitch / 2 - 0.35))
  const toX = (ts: number) => Math.max(0, Math.min(w, ((ts - startTs) / range) * w))
  const yTicks = volumeTicks(maxStack)

  // Hover: aggregate dots within a ±7px band around the cursor
  const hoverTs = hoverX != null ? startTs + (hoverX / w) * range : null
  const bandHalf = (7 / w) * range
  const nearbyDots = hoverTs != null ? dots.filter(d => Math.abs(d.ts - hoverTs) < bandHalf) : []
  const tooltipW = 280
  const tipLeft = Math.max(0, Math.min(mouseX - tooltipW / 2, containerW - tooltipW))

  return (
    <div className="rounded-lg border border-neutral-200/60 bg-neutral-50/30 px-4 py-3.5 relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3 text-[10px] text-neutral-400">
          <span className="uppercase tracking-[0.08em] font-bold text-neutral-500 text-[8.5px]">Volume over time</span>
          <span className="flex items-center gap-3">
            {sc.severe > 0 && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: SEV_COLOR.severe }} />{sc.severe} severe</span>}
            {sc.moderate > 0 && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: SEV_COLOR.moderate }} />{sc.moderate} moderate</span>}
            {sc.minor > 0 && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: SEV_COLOR.minor }} />{sc.minor} minor</span>}
          </span>
          {events.length > 0 && (
            <span className="flex items-center gap-3 border-l border-neutral-200 pl-3">
              {events.some(e => e.type === 'status') && <span className="flex items-center gap-1"><span className="inline-block w-0 h-0 border-l-[3px] border-r-[3px] border-b-[4px] border-l-transparent border-r-transparent border-b-emerald-500" />status</span>}
              {events.some(e => e.type === 'comment') && <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />comment</span>}
            </span>
          )}
        </div>
        <span className="text-[11px] font-mono tabular-nums text-neutral-500">
          {plotted === displayTotal ? `${plotted} occurrences` : `${plotted} plotted of ${displayTotal}`}
        </span>
      </div>

      {/* Dot-stack histogram */}
      <svg
        width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const px = e.clientX - rect.left
          setMouseX(px)
          setContainerW(rect.width)
          setHoverX((px / rect.width) * w)
          setHoverEvent(null)
        }}
        onMouseLeave={() => { setHoverX(null); setHoverEvent(null) }}
      >
        {/* Volume gridlines (y-axis = occurrence count) */}
        {yTicks.map((v) => {
          const y = axisY - v * dotPitch
          return (
            <g key={`y${v}`}>
              <line x1={0} y1={y} x2={w} y2={y} stroke="#eee" strokeWidth={0.5} />
              <text x={2} y={y - 1.5} fontSize={6.5} fill="#bbb" className="tabular-nums">{v}</text>
            </g>
          )
        })}
        <line x1={0} y1={axisY} x2={w} y2={axisY} stroke="#d4d4d4" strokeWidth={0.6} />

        {/* Hover band highlight */}
        {hoverX != null && (
          <rect x={hoverX - 7} y={plotTop} width={14} height={plotH} fill="#000" opacity={0.04} rx={2} />
        )}

        {/* Occurrence dots — stacked */}
        {dots.map((d, i) => {
          const cy = axisY - (d.stackIdx + 0.5) * dotPitch
          return <circle key={i} cx={d.cx} cy={cy} r={dotR} fill={SEV_COLOR[d.sev] || '#a3a3a3'} opacity={0.78} />
        })}

        {/* Event markers — status as dashed verticals, comments as axis dots */}
        {events.map((ev, i) => {
          const x = toX(ev.ts)
          const isHovered = hoverEvent === i
          if (ev.type === 'status') {
            return (
              <g key={`ev${i}`} onMouseEnter={() => { setHoverEvent(i); setHoverX(null) }} style={{ cursor: 'pointer' }}>
                <line x1={x} y1={plotTop} x2={x} y2={axisY} stroke={ev.color} strokeWidth={isHovered ? 1.5 : 0.9} strokeDasharray="3,2" opacity={isHovered ? 0.9 : 0.45} />
                <polygon points={`${x},${axisY - 6} ${x - 3.5},${axisY + 1} ${x + 3.5},${axisY + 1}`} fill={ev.color} opacity={isHovered ? 1 : 0.85} />
              </g>
            )
          }
          return (
            <g key={`ev${i}`} onMouseEnter={() => { setHoverEvent(i); setHoverX(null) }} style={{ cursor: 'pointer' }}>
              <circle cx={x} cy={axisY + 4} r={isHovered ? 3.5 : 2.6} fill={ev.color} opacity={isHovered ? 1 : 0.6} />
            </g>
          )
        })}

        {/* Axis ticks */}
        {ticks.map((t, i) => {
          const x = toX(t.ts)
          return (
            <text key={`t${i}`} x={x} y={h - 1} textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'} fontSize={7} fill="#a3a3a3">{t.label}</text>
          )
        })}
      </svg>

      {/* Hover tooltip — band of occurrences */}
      {nearbyDots.length > 0 && (
        <div className="absolute z-20 bg-neutral-900 text-white rounded-lg shadow-xl shadow-neutral-900/20 px-3.5 py-2.5 text-[10px] pointer-events-none border border-neutral-800" style={{ top: 28, left: tipLeft, width: tooltipW }}>
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-semibold text-[12px]">{nearbyDots.length} occurrence{nearbyDots.length !== 1 ? 's' : ''}</span>
            <span className="text-neutral-400 font-mono text-[9px]">{new Date(hoverTs!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })}</span>
          </div>
          <div className="flex gap-2 text-[10px] mb-1.5">
            {(() => { const s = nearbyDots.filter(d => d.sev === 'severe').length; return s > 0 ? <span className="text-red-300">{s} severe</span> : null })()}
            {(() => { const m = nearbyDots.filter(d => d.sev === 'moderate').length; return m > 0 ? <span className="text-amber-300">{m} moderate</span> : null })()}
            {(() => { const n = nearbyDots.filter(d => d.sev === 'minor').length; return n > 0 ? <span className="text-neutral-400">{n} minor</span> : null })()}
          </div>
          <div className="space-y-0.5 max-h-[80px] overflow-hidden border-t border-neutral-700 pt-1.5">
            {nearbyDots.slice(0, 3).map((d, j) => (
              <div key={j} className="text-neutral-400 truncate">{d.email}: {d.desc.slice(0, 60)}</div>
            ))}
            {nearbyDots.length > 3 && <div className="text-neutral-500">+{nearbyDots.length - 3} more</div>}
          </div>
        </div>
      )}
      {hoverEvent != null && events[hoverEvent] && (
        <div className="absolute z-20 bg-neutral-900 text-white rounded-lg shadow-xl shadow-neutral-900/20 px-3.5 py-2.5 text-[10px] max-w-[240px] pointer-events-none border border-neutral-800" style={{ top: 28, left: Math.max(0, Math.min(mouseX - 120, containerW - 240)) }}>
          <div className="font-mono text-neutral-300 mb-0.5">
            {new Date(events[hoverEvent].ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
          <div className="font-semibold" style={{ color: events[hoverEvent].color }}>
            {events[hoverEvent].type === 'status' ? `Status → ${events[hoverEvent].label}` : `Comment by ${events[hoverEvent].label}`}
          </div>
          {events[hoverEvent].detail && <div className="text-neutral-400 mt-0.5">{events[hoverEvent].detail}</div>}
        </div>
      )}
    </div>
  )
}

// ─── Combined event log (status changes + comments chronologically) ──────────

type TimelineEvent =
  | { type: 'comment'; text: string; author: string; createdAt: number }
  | { type: 'status'; status: string; author?: string; timestamp: number }

function EventLog({
  comments,
  statusHistory,
  issueNumber,
  userName,
}: {
  comments: { text: string; author: string; createdAt: number }[]
  statusHistory?: { status: string; timestamp: number; author?: string }[]
  issueNumber: number
  userName: string
}) {
  const addComment = useMutation(api.admin.addIssueComment)

  const events = useMemo<TimelineEvent[]>(() => {
    const all: TimelineEvent[] = []
    comments.forEach((c) => all.push({ type: 'comment', ...c }))
    statusHistory?.forEach((s) => all.push({ type: 'status', ...s }))
    all.sort((a, b) => {
      const ta = a.type === 'comment' ? a.createdAt : a.timestamp
      const tb = b.type === 'comment' ? b.createdAt : b.timestamp
      return tb - ta
    })
    return all
  }, [comments, statusHistory])

  return (
    <div>
      <div className="mb-4">
        <CommentInput onSubmit={async (text) => {
          await addComment({ issueNumber, text, author: userName })
        }} />
      </div>
      {events.length > 0 && (
        <div className="relative pl-5 border-l-2 border-neutral-100 space-y-3">
          {events.map((ev, i) => {
            if (ev.type === 'status') {
              const st = ev.status as IssueStatus
              return (
                <div key={`s-${i}`} className="relative">
                  <div className={`absolute -left-[23px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${STATUS_DOT[st] || 'bg-neutral-300'}`} />
                  <div className="flex items-center gap-1.5 text-[10.5px]">
                    <span className={`font-semibold ${
                      st === 'resolved' ? 'text-emerald-600' :
                      st === 'regressed' ? 'text-red-600' :
                      st === 'investigating' || st === 'in_progress' ? 'text-amber-600' :
                      'text-neutral-600'
                    }`}>
                      {STATUS_LABEL[st] || ev.status}
                    </span>
                    {ev.author && <span className="text-neutral-400">by {ev.author}</span>}
                    <span className="text-neutral-300 font-mono text-[10px] tabular-nums">{formatTime(ev.timestamp)}</span>
                  </div>
                </div>
              )
            }
            return (
              <div key={`c-${i}`} className="relative">
                <div className="absolute -left-[23px] top-2 w-2.5 h-2.5 rounded-full border-2 border-white bg-blue-500" />
                <div className="rounded-lg bg-white border border-neutral-200/60 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
                  <div className="flex items-center gap-2 px-3.5 py-2 bg-neutral-50/60 border-b border-neutral-100/60">
                    <span className="text-[10.5px] font-bold text-neutral-700">{ev.author}</span>
                    <span className="text-[9.5px] text-neutral-300 font-mono tabular-nums">{formatCommentTime(ev.createdAt)}</span>
                  </div>
                  <div className="px-3.5 py-3 text-[11.5px] text-neutral-600">
                    <MarkdownBody text={ev.text} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Issue detail (expanded row) ─────────────────────────────────────────────

function IssueDetail({
  issue, trackingEntry, userName, admins,
}: {
  issue: Issue
  trackingEntry?: TrackingEntry
  userName: string
  admins?: AdminUser[]
}) {
  const setStatus = useMutation(api.admin.setIssueStatus)
  const assignIssue = useMutation(api.admin.assignIssue)

  const occurrences = useQuery(
    api.admin.getGlobalIssueOccurrences,
    issue.issueNumber != null ? { issueNumber: issue.issueNumber } : 'skip',
  ) as Occurrence[] | undefined
  const occurrenceTimeline = useQuery(
    api.admin.getGlobalIssueOccurrenceTimeline,
    issue.issueNumber != null ? { issueNumber: issue.issueNumber } : 'skip',
  ) as Array<{ at: number; sev: string; email?: string }> | undefined
  // Full comments + status history are fetched per-issue (the list map is lite).
  const trackingDetail = useQuery(
    api.admin.getIssueTrackingDetail,
    issue.issueNumber != null ? { issueNumber: issue.issueNumber } : 'skip',
  )
  // Capped heavy array feeds the occurrence cards; the full {at,sev,email} timeline
  // feeds the histogram so every occurrence is plotted.
  const histogramOccurrences = (occurrenceTimeline && occurrenceTimeline.length > 0)
    ? occurrenceTimeline.map(e => ({ occurredAt: e.at, findingSeverity: e.sev, userEmail: e.email }))
    : (occurrences ?? [])

  const currentStatus = (trackingEntry?.status ?? 'open') as IssueStatus
  const currentAssignee = trackingEntry?.assignee
  const comments = trackingDetail?.comments ?? []
  const statusHistory = trackingDetail?.statusHistory

  const handleStatusChange = async (newStatus: IssueStatus) => {
    await setStatus({ issueNumber: issue.issueNumber ?? 0, status: newStatus })
  }

  const handleAssigneeChange = async (userId: string) => {
    await assignIssue({ issueNumber: issue.issueNumber ?? 0, assignee: userId || undefined })
  }

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(['analysis', 'occurrences']))
  const [occLimit, setOccLimit] = useState(5)

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  return (
    <div className="border-t border-neutral-200/40 bg-gradient-to-b from-neutral-50/40 to-white px-5 py-5 space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Controls bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[8.5px] uppercase tracking-[0.08em] text-neutral-400 font-semibold">Status</span>
          <div className="flex rounded-lg border border-neutral-200/60 overflow-hidden">
            {ISSUE_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                className={`text-[10px] px-2.5 py-[5px] border-r border-neutral-200/40 last:border-r-0 transition-all duration-150 ${
                  currentStatus === s
                    ? STATUS_STYLE[s] + ' font-semibold'
                    : 'bg-white text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
        {admins && admins.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[8.5px] uppercase tracking-[0.08em] text-neutral-400 font-semibold">Assignee</span>
            <select
              value={currentAssignee || ''}
              onChange={(e) => handleAssigneeChange(e.target.value)}
              className="text-[10px] border border-neutral-200/60 rounded-md px-2 py-[5px] bg-white text-neutral-600 cursor-pointer hover:border-neutral-300 transition-colors"
            >
              <option value="">Unassigned</option>
              {admins.map((a) => (
                <option key={a.userId} value={a.userId}>{a.name || a.email || a.userId}</option>
              ))}
            </select>
          </div>
        )}
        <Link
          to={`/admin/issues/${issue.issueNumber}`}
          className="ml-auto text-[10px] text-neutral-400 hover:text-blue-600 flex items-center gap-1 font-semibold px-2.5 py-1.5 rounded-md hover:bg-blue-50/80 transition-all"
        >
          Full detail <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {/* Occurrence volume over time */}
      {histogramOccurrences.length > 0 && (
        <OccurrenceTimeline occurrences={histogramOccurrences} statusHistory={statusHistory} comments={comments} totalCount={issue.occurrenceCount} />
      )}

      {/* Activity — first section, most recent first */}
      <SectionHeader label="Activity" count={comments.length + (statusHistory?.length ?? 0)} collapsed={collapsedSections.has('activity')} onToggle={() => toggleSection('activity')} />
      {!collapsedSections.has('activity') && (
        <EventLog
          comments={comments}
          statusHistory={statusHistory}
          issueNumber={issue.issueNumber ?? 0}
          userName={userName}
        />
      )}

      {/* Context blocks — collapsible, collapsed by default */}
      <SectionHeader label="Analysis" count={[issue.userImpact, issue.rootCauseHypothesis, issue.reproSteps, issue.suggestedInvestigation].filter(Boolean).length} collapsed={collapsedSections.has('analysis')} onToggle={() => toggleSection('analysis')} />
      {!collapsedSections.has('analysis') && (
        <div className="grid grid-cols-2 gap-2.5">
          <div className="space-y-2.5">
            {issue.userImpact && <ContextBlock label="User impact" body={issue.userImpact} accent="red" />}
            <ContextBlock label="Root cause hypothesis" body={issue.rootCauseHypothesis} />
          </div>
          <div className="space-y-2.5">
            {issue.reproSteps && <ContextBlock label="Repro steps" body={issue.reproSteps} mono />}
            {issue.suggestedInvestigation && <ContextBlock label="Suggested investigation" body={issue.suggestedInvestigation} accent="blue" />}
          </div>
        </div>
      )}

      {/* Occurrences — collapsible with progressive loading */}
      <SectionHeader label="Occurrences" count={issue.occurrenceCount} collapsed={collapsedSections.has('occurrences')} onToggle={() => toggleSection('occurrences')} />
      {!collapsedSections.has('occurrences') && (
        <div>
          {occurrences === undefined ? (
            <div className="flex items-center gap-2 text-xs text-neutral-400 italic">
              <Loader2 className="h-3 w-3 animate-spin" />
              loading occurrences...
            </div>
          ) : (
            <div className="space-y-1.5">
              {occurrences.slice(0, occLimit).map((o, i) => (
                <OccurrenceCard key={i} occ={o} />
              ))}
              {occurrences.length > occLimit && (
                <button
                  onClick={() => setOccLimit((l) => l + 10)}
                  className="w-full text-[10px] text-neutral-400 hover:text-neutral-600 text-center py-2.5 rounded-lg border border-dashed border-neutral-200/60 hover:border-neutral-300 hover:bg-neutral-50/80 transition-all font-mono tabular-nums font-medium"
                >
                  Show {Math.min(10, occurrences.length - occLimit)} more of {occurrences.length - occLimit} remaining
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Markdown renderer ───────────────────────────────────────────────────────

function MarkdownBody({ text }: { text: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">{children}</a>
        ),
        code: ({ children, className }) => {
          const isBlock = className?.includes('language-')
          return isBlock
            ? <code className="block bg-neutral-100 rounded px-2 py-1.5 my-1.5 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto">{children}</code>
            : <code className="bg-neutral-100 rounded px-1 py-0.5 text-[11px] font-mono">{children}</code>
        },
        pre: ({ children }) => <>{children}</>,
        ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-neutral-800">{children}</strong>,
        h1: ({ children }) => <div className="font-semibold text-neutral-800 text-sm mt-2 mb-1">{children}</div>,
        h2: ({ children }) => <div className="font-semibold text-neutral-800 text-xs mt-2 mb-1">{children}</div>,
        h3: ({ children }) => <div className="font-semibold text-neutral-700 text-xs mt-1.5 mb-0.5">{children}</div>,
        blockquote: ({ children }) => <blockquote className="border-l-2 border-neutral-300 pl-2 ml-1 text-neutral-500 italic">{children}</blockquote>,
        hr: () => <hr className="border-neutral-200 my-2" />,
        table: ({ children }) => <table className="w-full text-[11px] border-collapse my-2">{children}</table>,
        thead: ({ children }) => <thead className="bg-neutral-50">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="border-b border-neutral-100">{children}</tr>,
        th: ({ children }) => <th className="text-left px-2 py-1 font-semibold text-neutral-600 border-b border-neutral-200">{children}</th>,
        td: ({ children }) => <td className="px-2 py-1 text-neutral-700">{children}</td>,
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

// ─── Comment input ───────────────────────────────────────────────────────────

function CommentInput({ onSubmit }: { onSubmit: (text: string) => Promise<void> }) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`
    }
  }, [text])

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(text.trim())
      setText('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            handleSubmit()
          }
        }}
        placeholder="Add a comment (markdown supported)..."
        rows={2}
        className="w-full text-[11px] border border-neutral-200/60 rounded-lg px-3 py-2 bg-white placeholder:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-200 resize-none font-mono transition-shadow"
      />
      <button
        onClick={handleSubmit}
        disabled={submitting || !text.trim()}
        className="absolute bottom-2 right-2 text-[10px] px-2.5 py-1 rounded-md bg-neutral-900 text-white disabled:opacity-15 hover:bg-neutral-800 transition-all flex items-center gap-1 font-medium shadow-sm"
      >
        <Send className="h-2.5 w-2.5" />
        {submitting ? '...' : '⌘↵'}
      </button>
    </div>
  )
}

// ─── Tag autocomplete (projects / users) ─────────────────────────────────────

function TagAutocomplete<T extends { key: string; label: string; sub?: string }>({
  label,
  placeholder,
  selected,
  onAdd,
  onRemove,
  useSearch,
}: {
  label: string
  placeholder: string
  selected: T[]
  onAdd: (item: T) => void
  onRemove: (key: string) => void
  useSearch: (q: string) => T[]
}) {
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const results = useSearch(query)
  const selectedKeys = new Set(selected.map((s) => s.key))
  const filtered = results.filter((r) => !selectedKeys.has(r.key))

  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">{label}</label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selected.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
              {s.label}
              <button onClick={() => onRemove(s.key)} className="text-neutral-400 hover:text-neutral-600 ml-0.5"><X className="h-2.5 w-2.5" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={placeholder}
          className="w-full text-xs border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-200"
        />
        {focused && query.trim() && filtered.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded-md shadow-lg max-h-36 overflow-y-auto">
            {filtered.map((r) => (
              <button
                key={r.key}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onAdd(r); setQuery('') }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 flex items-center gap-2"
              >
                <span className="font-medium text-neutral-800">{r.label}</span>
                {r.sub && <span className="text-neutral-400 truncate">{r.sub}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

type ProjectTag = { key: string; label: string; sub?: string }
type UserTag = { key: string; label: string; sub?: string }

function useProjectSearch(q: string): ProjectTag[] {
  const results = useQuery(api.admin.searchProjectsAutocomplete, q.trim() ? { query: q.trim() } : 'skip')
  return useMemo(() => (results ?? []).map((r) => ({
    key: r.shortcode,
    label: r.shortcode,
    sub: r.title ?? undefined,
  })), [results])
}

function useUserSearch(q: string): UserTag[] {
  const results = useQuery(api.admin.searchUsersAutocomplete, q.trim() ? { query: q.trim() } : 'skip')
  return useMemo(() => (results ?? []).map((r) => ({
    key: r.email,
    label: r.email,
    sub: r.name ?? undefined,
  })), [results])
}

// ─── Create issue modal ──────────────────────────────────────────────────────

function CreateIssueModal({
  admins,
  categories,
  onClose,
}: {
  admins?: AdminUser[]
  categories: string[]
  onClose: () => void
}) {
  const createIssue = useMutation(api.admin.createIssue)
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<Severity>('P1')
  const [category, setCategory] = useState(categories[0] || '')
  const [customCategory, setCustomCategory] = useState('')
  const [description, setDescription] = useState('')
  const [rootCause, setRootCause] = useState('')
  const [tags, setTags] = useState('')
  const [assignee, setAssignee] = useState('')
  const [linkedProjects, setLinkedProjects] = useState<ProjectTag[]>([])
  const [linkedUsers, setLinkedUsers] = useState<UserTag[]>([])
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!title.trim() || submitting) return
    setSubmitting(true)
    try {
      const cat = customCategory.trim() || category
      await createIssue({
        title: title.trim(),
        severity,
        category: cat || 'uncategorized',
        description: description.trim() || undefined,
        rootCauseHypothesis: rootCause.trim() || undefined,
        tags: tags.trim() ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
        assignee: assignee || undefined,
        linkedProjects: linkedProjects.length ? linkedProjects.map((p) => p.key) : undefined,
        linkedUsers: linkedUsers.length ? linkedUsers.map((u) => u.key) : undefined,
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl shadow-neutral-900/10 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto border border-neutral-200/60 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100/60">
          <h2 className="text-[14px] font-bold text-neutral-900 tracking-tight">Create Issue</h2>
          <button onClick={onClose} className="text-neutral-300 hover:text-neutral-500 transition-colors p-0.5 rounded-md hover:bg-neutral-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue..."
              className="w-full text-sm border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-200"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Severity</label>
              <div className="flex gap-1">
                {(['P0', 'P1', 'P2'] as Severity[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSeverity(s)}
                    className={`flex-1 text-xs px-2 py-1.5 rounded border transition-colors ${
                      severity === s ? SEV_STYLE[s] + ' font-semibold' : 'bg-white text-neutral-400 border-neutral-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => { setCategory(e.target.value); setCustomCategory('') }}
                className="w-full text-xs border border-neutral-200 rounded-md px-2 py-1.5 bg-white"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="_custom">+ Custom</option>
              </select>
              {category === '_custom' && (
                <input
                  type="text"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="New category name"
                  className="w-full text-xs border border-neutral-200 rounded-md px-2 py-1.5 mt-1"
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Description (markdown)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed description with markdown support..."
              rows={4}
              className="w-full text-xs border border-neutral-200 rounded-md px-3 py-2 font-mono resize-none focus:outline-none focus:ring-2 focus:ring-neutral-200"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Root cause hypothesis</label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              placeholder="What do you think is causing this?"
              rows={2}
              className="w-full text-xs border border-neutral-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-neutral-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TagAutocomplete
              label="Linked projects"
              placeholder="Search by shortcode or title..."
              selected={linkedProjects}
              onAdd={(p) => setLinkedProjects((prev) => [...prev, p])}
              onRemove={(k) => setLinkedProjects((prev) => prev.filter((p) => p.key !== k))}
              useSearch={useProjectSearch}
            />
            <TagAutocomplete
              label="Linked users"
              placeholder="Search by email or name..."
              selected={linkedUsers}
              onAdd={(u) => setLinkedUsers((prev) => [...prev, u])}
              onRemove={(k) => setLinkedUsers((prev) => prev.filter((u) => u.key !== k))}
              useSearch={useUserSearch}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="prompt, ux, generation..."
                className="w-full text-xs border border-neutral-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-200"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-neutral-500 block mb-1">Assignee</label>
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="w-full text-xs border border-neutral-200 rounded-md px-2 py-2 bg-white"
              >
                <option value="">Unassigned</option>
                {admins?.map((a) => (
                  <option key={a.userId} value={a.userId}>{a.name || a.email || a.userId}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 py-3.5 border-t border-neutral-100/60 bg-neutral-50/30">
          <button onClick={onClose} className="text-[11px] px-3.5 py-[7px] rounded-lg border border-neutral-200/80 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-all font-medium">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            className="text-[11px] px-4 py-[7px] rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-30 transition-all font-semibold shadow-sm"
          >
            {submitting ? 'Creating...' : 'Create Issue'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Active filter chips ────────────────────────────────────────────────────

function ActiveFilters({ filters, clearAll, count }: { filters: { label: string; clear: () => void }[]; clearAll: () => void; count?: number }) {
  if (filters.length === 0) return null
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {count != null && count > 0 && (
        <span className="text-[9px] font-semibold text-neutral-500 bg-neutral-100/80 px-2 py-0.5 rounded-full tabular-nums mr-0.5 flex items-center gap-1">
          <Filter className="h-2 w-2" />{count}
        </span>
      )}
      {filters.map((f) => (
        <span key={f.label} className="inline-flex items-center gap-1 text-[10px] px-2 py-[3px] rounded-md bg-blue-50/80 text-blue-600 border border-blue-100/60 font-medium">
          {f.label}
          <button onClick={f.clear} className="text-blue-300 hover:text-blue-600 transition-colors"><X className="h-2.5 w-2.5" /></button>
        </span>
      ))}
      {filters.length > 1 && (
        <button onClick={clearAll} className="text-[9px] text-neutral-400 hover:text-neutral-600 ml-0.5 font-medium transition-colors">Clear all</button>
      )}
    </div>
  )
}

// ─── Collapsible section header ─────────────────────────────────────────────

function SectionHeader({ label, count, collapsed, onToggle }: { label: string; count: number; collapsed: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} className="flex items-center gap-2 group w-full py-1.5">
      {collapsed ? <ChevronRight className="h-3 w-3 text-neutral-300 group-hover:text-neutral-500 transition-colors" /> : <ChevronDown className="h-3 w-3 text-neutral-400" />}
      <span className="text-[8.5px] uppercase tracking-[0.08em] text-neutral-400 group-hover:text-neutral-600 font-bold transition-colors">{label}</span>
      {count > 0 && <span className="text-[8.5px] text-neutral-400 tabular-nums font-mono bg-neutral-100/60 px-1.5 py-[1px] rounded-md font-semibold">{count}</span>}
      <span className="flex-1 border-t border-neutral-100/60" />
    </button>
  )
}

// ─── Small components ────────────────────────────────────────────────────────

function ContextBlock({
  label, body, accent, mono,
}: {
  label: string; body: string; accent?: 'red' | 'blue'; mono?: boolean
}) {
  const border = accent === 'red' ? 'border-l-red-400' : accent === 'blue' ? 'border-l-blue-400' : 'border-l-neutral-200/80'
  const labelClass = accent === 'red' ? 'text-red-600/80' : accent === 'blue' ? 'text-blue-600/80' : 'text-neutral-400'
  const bodyClass = mono ? 'font-mono text-[11px] text-neutral-600 whitespace-pre-wrap leading-relaxed' : 'text-[11.5px] text-neutral-600 leading-relaxed whitespace-pre-wrap'
  return (
    <div className={`rounded-lg border border-neutral-100/80 border-l-2 ${border} bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.02)]`}>
      <div className={`text-[8.5px] uppercase tracking-[0.08em] mb-2 font-bold ${labelClass}`}>{label}</div>
      <p className={bodyClass}>{body}</p>
    </div>
  )
}

function StatusTab({ active, onClick, children, count, dim }: {
  active: boolean; onClick: () => void; children: React.ReactNode; count?: number; dim?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] px-3 py-2 font-medium border-b-2 transition-all duration-150 inline-flex items-center gap-1 ${
        active
          ? 'border-neutral-900 text-neutral-900'
          : 'border-transparent text-neutral-400 hover:text-neutral-600 hover:border-neutral-200'
      } ${dim && !active ? 'opacity-30' : ''}`}
    >
      {children}
      {count != null && count > 0 && (
        <span className={`text-[9px] tabular-nums font-mono ml-0.5 ${active ? 'text-neutral-500' : 'text-neutral-300'}`}>{count}</span>
      )}
    </button>
  )
}

function FilterSelect({ value, onChange, placeholder, options }: {
  value: string; onChange: (v: string) => void; placeholder: string; options: { value: string; label: string }[]
}) {
  const isActive = value !== 'ALL'
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`text-[10px] border rounded-md px-2 py-[5px] cursor-pointer transition-colors ${
        isActive ? 'border-blue-200 bg-blue-50/50 text-blue-700 font-medium' : 'border-neutral-200/80 bg-white text-neutral-500 hover:border-neutral-300'
      }`}
    >
      <option value="ALL">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function Kbd({ k, k2, label, label2 }: { k: string; k2?: string; label?: string; label2?: string }) {
  const kStyle = "px-1.5 py-0.5 rounded-[4px] bg-neutral-100/60 text-neutral-400 font-mono text-[8px] border border-neutral-200/40 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
  return (
    <span className="text-[9px] text-neutral-300 flex items-center gap-0.5">
      <kbd className={kStyle}>{k}</kbd>
      {k2 && <>{label || ''}<kbd className={kStyle}>{k2}</kbd></>}
      {label2 && <span className="ml-0.5">{label2}</span>}
    </span>
  )
}

function KeyboardShortcutsModal({ onClose }: { onClose: () => void }) {
  const shortcuts = [
    { keys: ['j', 'k'], desc: 'Navigate up / down' },
    { keys: ['Enter'], desc: 'Expand / collapse issue' },
    { keys: ['x'], desc: 'Select / deselect issue' },
    { keys: ['c'], desc: 'Create new issue' },
    { keys: ['/'], desc: 'Focus search' },
    { keys: ['?'], desc: 'Toggle shortcuts help' },
    { keys: ['Esc'], desc: 'Clear selection / close' },
  ]
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl shadow-neutral-900/10 w-full max-w-sm mx-4 border border-neutral-200/60 animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100/60">
          <h2 className="text-[13px] font-bold text-neutral-900 tracking-tight">Keyboard Shortcuts</h2>
          <button onClick={onClose} className="text-neutral-300 hover:text-neutral-500 transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-2.5">
          {shortcuts.map(s => (
            <div key={s.desc} className="flex items-center justify-between">
              <span className="text-[12px] text-neutral-500">{s.desc}</span>
              <div className="flex items-center gap-1">
                {s.keys.map(k => (
                  <kbd key={k} className="px-2 py-0.5 rounded-md bg-neutral-100/80 text-neutral-600 font-mono text-[10.5px] border border-neutral-200/60 min-w-[26px] text-center shadow-[0_1px_0_rgba(0,0,0,0.04)] font-semibold">{k}</kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function OccurrenceCard({ occ }: { occ: Occurrence }) {
  const [expanded, setExpanded] = useState(false)
  const projectHref = `/admin/videos/detail/${occ.shortcode || occ.workflowId}`
  const descTruncated = !expanded && occ.description.length > 180
  const evTruncated = !expanded && occ.evidence && occ.evidence.length > 120

  return (
    <div className="rounded-lg border border-neutral-200/60 bg-white px-3.5 py-3 hover:border-neutral-300/80 hover:shadow-[0_2px_6px_rgba(0,0,0,0.04)] transition-all duration-200 group">
      <div className="flex items-start gap-2.5">
        <div className={`w-[3px] self-stretch rounded-full shrink-0 ${
          occ.findingSeverity === 'severe' ? 'bg-red-500' : occ.findingSeverity === 'moderate' ? 'bg-amber-400' : 'bg-neutral-200'
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              {occ.userGoal && (
                <div className="text-[10px] text-neutral-400 mb-0.5 font-medium uppercase tracking-wide">
                  {occ.userGoal}
                </div>
              )}
              <p className="text-[12px] text-neutral-800 leading-relaxed">
                {descTruncated ? occ.description.slice(0, 180) + '...' : occ.description}
              </p>
            </div>
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded shrink-0 uppercase tracking-wide ${FINDING_SEV_STYLE[occ.findingSeverity] || ''}`}>
              {occ.findingSeverity}
            </span>
          </div>
          {occ.evidence && (
            <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed border-l border-neutral-200 pl-2 italic">
              {evTruncated ? occ.evidence.slice(0, 120) + '...' : occ.evidence}
            </p>
          )}
          {(descTruncated || evTruncated) && (
            <button onClick={() => setExpanded(true)} className="text-[10px] text-blue-500 hover:text-blue-600 mt-1">show full</button>
          )}
          <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-neutral-400 flex-wrap">
            <a href={projectHref} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 hover:underline inline-flex items-center gap-0.5 font-medium transition-colors">
              {occ.projectTitle}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
            <span className="w-px h-2.5 bg-neutral-200/60" />
            <span className="font-mono text-neutral-400">{occ.userEmail}</span>
            <span className="w-px h-2.5 bg-neutral-200/60" />
            <span className="font-mono tabular-nums">{occ.sessionScore}/10</span>
            {typeof occ.occurredAt === 'number' && (
              <>
                <span className="w-px h-2.5 bg-neutral-200/60" />
                <span className="font-mono tabular-nums">{formatTime(occ.occurredAt)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
